<template>
  <settings-form :breadcrumbs="breadcrumbs">
    <template #actions>
      <v-btn
        class="mr-2"
        color="success"
        :disabled="!isChanged"
        @click="save"
      >
        <v-icon>mdi-check</v-icon>
        {{ $t('button.save_changes') }}
      </v-btn>
      <v-btn
        class="mr-2"
        color="error"
        :disabled="isNew || isActive"
        @click="deleteConfirmation = true"
      >
        <v-icon>mdi-delete</v-icon>
        <span class="d-none d-lg-block">{{ $t('button.delete_user') }}</span>
      </v-btn>
    </template>
    <template #content>
      <!--  -->
      <confirmation-dialog
        :visible="deleteConfirmation"
        :message="$t('message.confirm_delete_user')"
        @confirm="remove"
        @close="deleteConfirmation = false"
      />
      <!--  -->
      <change-password-dialog
        :visible="changePasswordDialog"
        :username="u.username"
        @close="changePasswordDialog = false"
      />
      <validation-observer ref="observer">
        <settings-group
          :title="$t('label.general_settings')"
          :subtitle="$t('settings.user.general_settings_subtitle')"
          gutter
        >
          <v-col cols="6">
            <validated-text-field
              v-model="u.email"
              rules="required|max:254|validEmail"
              :label="$t('label.email')"
              :disabled="!isNew"
              required
            />
          </v-col>
          <v-col cols="6">
            <validated-text-field
              v-model="u.name"
              rules="required|min:2|max:255"
              class="pt-0"
              :label="$t('label.first_name')"
            />
          </v-col>
          <v-col
            v-if="isActive"
            class="py-0"
            cols="12"
          >
            <v-btn
              outlined
              color="primary"
              :disabled="isNew"
              @click="changePasswordDialog = true"
            >
              {{ $t('button.update_password') }}
            </v-btn>
          </v-col>
        </settings-group>

        <!-- Two-Factor Authentication (only for active user) -->
        <template v-if="isActive">
          <settings-group
            gutter
            :title="$t('settings.user.twofa_title')"
            :subtitle="$t('settings.user.twofa_subtitle')"
          >
            <v-col
              v-if="twofaLoading"
              cols="12"
              class="text-center py-4"
            >
              <v-progress-circular
                indeterminate
                color="primary"
              />
            </v-col>
            <template v-else-if="twofaSettings">
              <auth-two-f-a-method-card
                v-if="twofaSettings.enabledMethods.includes('email')"
                class="d-flex flex-grow-1 w-100"
                :method-enabled="twofaSettings.enabledMethods.includes('email')"
                icon="mdi-email-outline"
                :title="$t('settings.user.twofa_verification_email_label')"
                :subtitle="u.email || '—'"
              />
              <auth-two-f-a-method-card
                v-if="twofaSettings.enabledMethods.includes('sms')"
                class="d-flex flex-grow-1 w-100"
                :method-enabled="twofaSettings.enabledMethods.includes('sms')"
                icon="mdi-message-text-outline"
                :title="$t('settings.user.twofa_verification_phone_label')"
                :subtitle="twofaSettings.phone || '—'"
              />
              <auth-two-f-a-method-card
                v-if="twofaSettings.enabledMethods.includes('passkey')"
                class="d-flex flex-column flex-grow-1 w-100"
                :method-enabled="twofaSettings.enabledMethods.includes('passkey')"
                icon="mdi-key-variant"
                :title="$t('settings.user.twofa_passkeys_label')"
                :subtitle="passkeysSubtitle"
              />

              <v-col
                v-if="twofaSettings.enabledMethods.length === 0"
                cols="12"
                class="py-2 text-body-2 text--secondary"
              >
                {{ $t('settings.user.twofa_no_methods_enabled') }}
              </v-col>
              <v-col
                v-if="twofaSettings.canManageTwoFA === true"
                cols="12"
                class="pt-2"
              >
                <v-btn
                  outlined
                  color="primary"
                  @click="twofaDialogVisible = true"
                >
                  {{ twofaSettings.enabledMethods.length === 0 ? $t('settings.user.twofa_setup_button') : $t('settings.user.twofa_manage_button') }}
                </v-btn>
              </v-col>
            </template>
          </settings-group>
          <auth-two-f-a-setup-dialog
            v-if="twofaDialogVisible"
            @close="twofaDialogVisible = false"
            @updated="onTwoFAUpdated"
          />
        </template>

        <settings-group
          :title="$t('settings.user.permissions_title')"
          :subtitle="$t('settings.user.permissions_subtitle')"
        >
          <v-col
            v-for="(o, i) in userOptions"
            :key="`user_option_${i}`"
            class="py-2"
            cols="12"
          >
            <v-switch
              v-model="u[o.value]"
              hide-details
              :label="o.title"
              class="pt-0 mt-0"
            />
            <p class="pt-0 mt-1 ml-12">
              {{ o.subtitle }}
            </p>
          </v-col>
        </settings-group>

        <settings-group
          :title="$t('settings.user.privacy_screen_title')"
          :subtitle="$t('settings.user.privacy_screen_subtitle')"
        >
          <v-col
            cols="12"
            md="6"
            class="py-0"
          >
            <v-select
              v-model="u.privacyScreenTimeout"
              :items="privacyItems"
              :label="$t('label.show_privacy_screen_after')"
            />
          </v-col>
        </settings-group>
      </validation-observer>
    </template>
  </settings-form>
</template>

<script lang="ts">
import Vue from 'vue';
import UnsavedGuard from '@/mixins/unsaved-guard';
import SettingsBreadcrumbs from '@/mixins/settings-breadcrumbs';
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue';
import ChangePasswordDialog from '@/components/views/settings/ChangePasswordDialog.vue';
import AuthTwoFASetupDialog from '@/components/views/settings/AuthTwoFASetupDialog.vue';
import User from '@/model/User';
import { performAction, performSaveAction } from '@/services/vue-utils';
import { isModelEqualToModel, cloneModel } from '@/model/model-utils';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import SettingsGroup from '@/components/layouts/SettingsGroup.vue';
import httpClient from '@/api/http-client';
import { IAuth2FASettings } from '@/model/AuthSettings';
import AuthTwoFAMethodCard from '@/components/views/settings/AuthTwoFAMethodCard.vue';

export default Vue.extend({
  name: 'User',
  components: {
    AuthTwoFAMethodCard, ConfirmationDialog, ChangePasswordDialog, AuthTwoFASetupDialog, SettingsForm, SettingsGroup,
  },
  mixins: [UnsavedGuard, SettingsBreadcrumbs],
  data() {
    return {
      deleteConfirmation: false,
      changePasswordDialog: false,
      u: new User(),
      oldu: new User(),
      unwatch: Function as (() => void),
      twofaSettings: null as IAuth2FASettings | null,
      twofaLoading: false,
      twofaDialogVisible: false,
      privacyItems: [
        { text: this.$t('label.privacy_screen_duration_never'), value: 0 },
        ...[1, 5, 10, 30, 60].map((n) => ({ text: this.$tc('label.x_seconds', n), value: n })),
      ],
      userOptions: [
        {
          title: this.$t('label.allow_signin'),
          subtitle: this.$t('settings.user.allow_signin_help'),
          value: 'allowSignIn' as keyof User,
        },
        {
          title: this.$t('label.allow_reservations'),
          value: 'allowReservations' as keyof User,
          subtitle: this.$t('settings.user.allow_reservations_help'),
        },
        {
          title: this.$i18n.t('label.allow_day_notes'),
          value: 'allowDayNotes' as keyof User,
          subtitle: this.$t('settings.user.allow_day_notes_help'),
        },
        {
          title: this.$i18n.t('label.allow_settings'),
          value: 'allowSettings' as keyof User,
          subtitle: this.$t('settings.user.allow_settings_help'),
        },
        {
          title: this.$i18n.t('label.allow_billing'),
          value: 'allowBilling' as keyof User,
          subtitle: this.$t('settings.user.allow_billing_help'),
        },
        {
          title: this.$i18n.t('label.shared_user'),
          value: 'sharedUser' as keyof User,
          subtitle: this.$t('settings.user.shared_user_help'),
        },
        {
          title: this.$i18n.t('label.user_newsletter_subscription'),
          value: 'newsletterSubscription' as keyof User,
          subtitle: this.$t('settings.user.newsletter_subscription_help'),
        },
      ],
      breadcrumbs: [] as any[],
    };
  },
  computed: {
    isNew(): boolean {
      return !this.u.id;
    },
    isChanged(): boolean {
      return !isModelEqualToModel(this.u, this.oldu);
    },
    isActive(): boolean {
      const activeUser = this.$tstore.getters.activeUser as User | undefined;
      return this.u.id === activeUser?.id;
    },
    passkeysSubtitle(): string {
      return this.twofaSettings?.passkeys?.length
        ? (() => {
          const { passkeys } = this.twofaSettings;
          const names = passkeys.map(
            (pk, i) => pk.label || `${this.$t('settings.user.twofa_passkey_unnamed')}#${i + 1}`,
          );

          const visible = names.slice(0, 2);
          const remaining = names.length - visible.length;

          return remaining > 0
            ? `${visible.join(', ')} (+${remaining})`
            : visible.join(', ');
        })()
        : '';
    },
  },
  created() {
    this.unwatch = this.$tstore.watch((state, getters) => getters.isLoaded, (newValue, oldValue) => { this.reset(); });
  },
  beforeDestroy() {
    this.unwatch();
  },
  mounted() {
    this.reset();
  },
  methods: {
    async save() {
      const ok = await performSaveAction(
        this.$refs.observer,
        async () => this.$tstore.dispatch('sendUser', { entity: this.u }),
      );

      if (ok) {
        this.reset();
        this.$router.back();
      }
    },
    reset() {
      this.changePasswordDialog = false;

      const id = Number(this.$route.params.id) || 0;
      const u = this.$tstore.state.settings.users.find((o: User) => o.id === id) ?? new User();
      if (!u.id) {
        this.userOptions.forEach((o) => { (u as any)[o.value] = true; });
      }
      this.u = cloneModel(u);
      this.oldu = u;

      this.breadcrumbs[2].text = this.u.name ?? this.$i18n.t('button.add_new_user');

      this.loadTwoFA();
    },
    async remove() {
      const ok = await performAction(
        this.$tc('message.success_user_deleted'),
        this.$tc('error.deleting_user'),
        async () => this.$tstore.dispatch('sendUser', { entity: this.u, delete: true }),
      );
      if (ok) {
        this.reset();
        this.$router.back();
      }
    },
    async loadTwoFA() {
      if (!this.isActive) return;
      this.twofaLoading = true;
      try {
        this.twofaSettings = await httpClient.authGetSettings();
      } catch (e) {
        console.error('loadTwoFA:', e);
      } finally {
        this.twofaLoading = false;
      }
    },
    onTwoFAUpdated(settings: IAuth2FASettings) {
      this.twofaSettings = settings;
    },
  },
});
</script>
